import { createContext, useContext, useReducer, useMemo } from "react";

const PrayerContext = createContext(null);

const initialPrayerState = {
  prayerLog: {},
  notes: {},
};

// Ensure prayer state is valid and has no null/undefined values
function validatePrayerState(state) {
  if (!state || typeof state !== 'object') {
    return { ...initialPrayerState };
  }
  return {
    prayerLog: typeof state?.prayerLog === 'object' && state.prayerLog !== null
      ? state.prayerLog
      : {},
    notes: typeof state?.notes === 'object' && state.notes !== null
      ? state.notes
      : {},
  };
}

function prayerReducer(state, action) {
  switch (action.type) {
    case "TOGGLE_PRAYER": {
      const { iso, prayer, completed } = action.payload;
      const currentState = state.prayerLog[iso]?.[prayer] || false;
      const newState = completed !== null ? completed : !currentState;

      return {
        ...state,
        prayerLog: {
          ...state.prayerLog,
          [iso]: {
            ...state.prayerLog[iso],
            [prayer]: newState,
          },
        },
      };
    }

    case "SET_NOTE": {
      const { iso, note } = action.payload;

      return {
        ...state,
        notes: {
          ...state.notes,
          [iso]: note,
        },
      };
    }

    case "HYDRATE_PRAYER":
      return action.payload;

    default:
      return state;
  }
}

export function PrayerProvider({ children, initialState = initialPrayerState }) {
  // Validate and sanitize initial state
  const validatedState = useMemo(() => validatePrayerState(initialState), [initialState]);
  const [state, dispatch] = useReducer(prayerReducer, validatedState);

  const actions = {
    togglePrayer: (iso, prayer, completed = null) => {
      dispatch({
        type: "TOGGLE_PRAYER",
        payload: { iso, prayer, completed },
      });
    },

    setNote: (iso, note) => {
      dispatch({
        type: "SET_NOTE",
        payload: { iso, note },
      });
    },

    hydratePrayer: (savedState) => {
      dispatch({
        type: "HYDRATE_PRAYER",
        payload: savedState,
      });
    },
  };

  // Memoize provider value - prevents unnecessary re-renders in other contexts
  const value = useMemo(
    () => ({
      state,
      actions,
    }),
    [state]
  );

  return (
    <PrayerContext.Provider value={value}>{children}</PrayerContext.Provider>
  );
}

export function usePrayerContext() {
  const context = useContext(PrayerContext);
  if (!context) {
    throw new Error("usePrayerContext must be used within PrayerProvider");
  }
  return context;
}
